import React, { useContext, useEffect, useRef, useState } from 'react';
import {
  ComponentRendering,
  Image,
  ImageField,
  Field,
  SitecoreContext,
  RichText,
} from '@sitecore-jss/sitecore-jss-react';
import './index.scss';
import { Button, Grid } from '@mui/material';
import ReactToPrint from 'react-to-print';
import { addEllipsis, getCurrencyInUSD, getDateTimePartsFromIso } from '../../utils/helper';
import { getAPICall } from '../../dataFetcher/axiosMethodCalls';
import LoadingSection from '../LoadingSection/LoadingSection';
import nextId from 'react-id-generator';
import { GState, jsPDF } from 'jspdf';
import DOMPurify from 'dompurify';
import LoadingIcon from '../LoadingIcon/LoadingIcon';
import { APIWebstoreContext } from '../../context/globalContext';
import { LobConfigurationProps } from '../PaymentReceipt/PaymentReceipt';
import { PAYMENT_TYPES, PURCHASE_ORDER, TRAINING_IDENTIFIER } from '../../utils/constants';
import { MODALITY_TITLES } from '../../utils/ProductTypes';
import html2pdf from 'html2pdf.js';
import { IPaymentReceipt } from '../../model/IUnifiedPaymentReceipt';

export type PaymentReceiptProps = {
  fields: {
    PrintButtonText: Field<string>;
    DownloadButtonText: Field<string>;
    ContactSupport: Field<string>;
    MailingAddressText: Field<string>;
    WireTransferInstruction: Field<string>;
    BillingAddressText: Field<String>;
    LOBSpecificConfiguration: Array<LobConfigurationProps>;
  };
  sitecoreContext: SitecoreContext & { pageEditing: boolean };
  rendering: ComponentRendering;
};

const UnifiedPaymentReceipt = ({ fields, rendering }: PaymentReceiptProps): JSX.Element => {
  const printRef = useRef<HTMLDivElement>(null);
  const [paymentReceiptDetails, setPaymentReceiptDetails] = useState<IPaymentReceipt>();
  const [isLoading, setIsLoading] = useState(true);
  const [isWorking, setIsWorking] = useState(false);
  const [downloadWidth, setDownloadWidth] = useState('1em');
  const { state } = useContext(APIWebstoreContext);

  const getLobConfig = (lob: string): LobConfigurationProps | undefined => {
    return fields?.LOBSpecificConfiguration?.find(
      (config) => config.fields?.LOBIdentifier?.value === lob
    );
  };

  function replaceFieldText(
    field: Field<string> | undefined,
    orderId?: string | undefined
  ): Field<string> | undefined {
    if (!field?.value) return undefined;

    return {
      ...field,
      value: field.value.replace('##INVOICE_NUMBER##', (orderId && orderId) || ''),
    };
  }

  function convertDate(date: string) {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  function getPaymentReceiptDetails() {
    let referenceNo;
    let search = window.location.search;
    let params = new URLSearchParams(search);
    if (params.get('referenceNo')) {
      referenceNo = params.get('referenceNo');
    }

    if (referenceNo === undefined || referenceNo === '') {
      setIsLoading(false);
      return;
    }

    getAPICall(`${process.env.REACT_APP_CART_URL}/CartOrder/${referenceNo}/unifiedinvoice`)
      .then(function (response: any) {
        if (response && response.status === 200) {
          setPaymentReceiptDetails(response.data);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }

  useEffect(() => {
    getPaymentReceiptDetails();
  }, []);

  function getFederalTaxInfo(federalTaxInstructions?: Field<string>) {
    const rawHtml = federalTaxInstructions?.value ?? '';
    const sanitizedHtml = DOMPurify.sanitize(rawHtml);

    return <div dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
  }

  function getInvoiceAddressSection(
    paymentType: string,
    paymentProcessed: boolean,
    isVerifoneOrder: boolean,
    lob: string,
    orderId: string
  ) {
    if (paymentType === PURCHASE_ORDER && paymentProcessed === false && !isVerifoneOrder) {
      return (
        <div className="invoiceAddressSection">
          {(getLobConfig(lob)?.fields?.CheckMailToAddress || fields.ContactSupport.value) && (
            <div className="flex-start-justifybetween bottomSpacing invoiceAddressWidth">
              <Grid container className="mailAddressesArea">
                <Grid item>
                  <RichText
                    field={replaceFieldText(getLobConfig(lob)?.fields?.CheckMailToAddress, orderId)}
                  />
                </Grid>
                <Grid item>
                  <RichText field={fields?.ContactSupport} />
                </Grid>
              </Grid>
            </div>
          )}

          {getLobConfig(lob)?.fields?.WireTransferMailToAddress && (
            <div className="invoiceAddressWidth wireTransferWidth">
              <Grid container>
                <Grid item>
                  <RichText
                    field={replaceFieldText(
                      getLobConfig(lob)?.fields?.WireTransferMailToAddress,
                      orderId
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          )}

          {getLobConfig(lob)?.fields?.CorrespondenceToAddress && (
            <div className="invoiceAddressWidth wireTransferWidth">
              <Grid container>
                <Grid item>
                  <RichText
                    field={replaceFieldText(
                      getLobConfig(lob)?.fields?.CorrespondenceToAddress,
                      orderId
                    )}
                  />
                </Grid>
              </Grid>
            </div>
          )}
        </div>
      );
    } else {
      return <></>;
    }
  }

  function getSealSection(
    paymentType?: string,
    isVerifoneOrder?: boolean,
    stampField?: ImageField
  ) {
    const renderStampImage = (stampField: ImageField) => {
      if (stampField?.value) {
        return <Image className="stampImage" field={stampField.value} />;
      }
      return <></>;
    };

    if (isVerifoneOrder) {
      return (
        <div>
          <div className="paid-info-section">
            {paymentType === PURCHASE_ORDER &&
              (fields?.WireTransferInstruction?.value || fields?.ContactSupport?.value) && (
                <div className="left-section">
                  <RichText className="wire-transfer-ins" field={fields?.WireTransferInstruction} />
                  <Grid className="wire-transfer-ins" item>
                    <RichText field={fields?.ContactSupport} />
                  </Grid>
                </div>
              )}
            {stampField && renderStampImage(stampField)}
          </div>
        </div>
      );
    } else {
      return <div className="paid-info">{stampField && renderStampImage(stampField)}</div>;
    }
  }

  const handleDownload = () => {
    if (typeof window !== 'undefined') {
      const html2pdf = require('html2pdf.js');
      setIsWorking(true);

      const content = printRef.current;

      if (content) {
        const options = {
          margin: 0,

          html2canvas: {
            useCORS: true,
            scale: 4,
            letterRendering: true,
          },

          jsPDF: {
            unit: 'pt',
            format: [content.offsetWidth / 1.4, content.offsetHeight / 1.3],
            orientation: 'portrait',
          },
        };

        html2pdf()
          .set(options)
          .from(content)
          .save(`Receipt-${paymentReceiptDetails?.unifiedReferenceNumber}.pdf`)
          .then(() => setIsWorking(false));
      }
    }
  };

  const InvoicePrinting = (): JSX.Element => {
    const lobOrderReceipts = paymentReceiptDetails?.lobOrderReceipts;

    if (!lobOrderReceipts || lobOrderReceipts.length === 0) {
      return <div>No payment receipt details available</div>;
    }

    return (
      <>
        {lobOrderReceipts.map((lobOrderReceipt) => {
          return lobOrderReceipt.invoices.map((paymentReceipt) => {
            const {
              payment,
              orderId,
              dateOrdered,
              dateRefunded,
              total,
              subTotal,
              memberDiscount,
              taxTotal,
              shippingTotal,
              receiptItems,
              shipFromAddress,
              billingAddress,
              isNoCostOrder,
              isVerifoneOrder,
              companyName,
              firstName,
              lastname,
            } = paymentReceipt;

            const paymentType = payment?.paymentType;
            const creditCardDesc = `${payment?.cardType?.toUpperCase()} ending ${
              payment?.last4DigitsCCNumber
            }`;

            const verifonePaymentType =
              state?.sitecoreDictionaryData?.[payment?.verifonePaymentType ?? ''];

            const paymentDescription = verifonePaymentType?.length
              ? `${verifonePaymentType} ${creditCardDesc}`
              : `Credit Card: ${creditCardDesc}`;

            const paymentProcessed = payment?.paymentProcessed;
            const shipToAddress = receiptItems[0].shipToAddress;

            const displayEmailAddresses = (emailAddresses: string[]) => {
              const emailCount = emailAddresses.reduce((count, email) => {
                const normalizedEmail = email.toLowerCase();
                count[normalizedEmail] = (count[normalizedEmail] || 0) + 1;
                return count;
              }, {} as Record<string, number>);

              return (
                <div key={nextId()}>
                  <span>
                    {Object.entries(emailCount)
                      .map(([email, count]) => `${email} (${count})`)
                      .join(', ')}
                  </span>
                </div>
              );
            };

            return (
              <div
                key={nextId()}
                className="main-area"
                style={{
                  ...(isWorking && { marginBottom: '2em' }),
                }}
              >
                <div className="invoice-content-area">
                  <div className="invoice-content">
                    <div className="header">
                      {paymentType === PURCHASE_ORDER && paymentProcessed === false
                        ? 'Pro Forma Invoice #'
                        : 'Receipt #'}
                      {orderId && orderId}
                      <div className="invoice-ref-header">
                        {'Reference #'}
                        {paymentReceiptDetails?.unifiedReferenceNumber &&
                          paymentReceiptDetails?.unifiedReferenceNumber}
                      </div>
                    </div>
                    {getLobConfig(lobOrderReceipt.lob)?.fields?.Logo && (
                      <Image
                        className="api-logo"
                        field={getLobConfig(lobOrderReceipt.lob)?.fields?.Logo.value}
                      />
                    )}
                  </div>
                  <br />

                  {paymentType === PURCHASE_ORDER && paymentProcessed === false && (
                    <div className="content-100">
                      {getLobConfig(lobOrderReceipt.lob)?.fields?.InvoiceNotice && (
                        <RichText
                          field={replaceFieldText(
                            getLobConfig(lobOrderReceipt.lob)?.fields?.InvoiceNotice,
                            orderId
                          )}
                        />
                      )}
                      <br />
                    </div>
                  )}

                  <div className="flex-start-justifybetween">
                    {shipToAddress && (
                      <div>
                        <div className="sub-header">{fields.MailingAddressText?.value}</div>
                        <div className="address-content">
                          {shipToAddress.firstName && shipToAddress.lastName && (
                            <div>{`${shipToAddress.firstName} ${shipToAddress.lastName}`}</div>
                          )}
                          <div>{shipToAddress.street1}</div>
                          <div>{shipToAddress.street2}</div>
                          <div>{`${shipToAddress.city}, ${shipToAddress.state} ${shipToAddress.zip}`}</div>
                          <div>{shipToAddress.country}</div>
                          <div>{shipToAddress.phone}</div>
                        </div>
                      </div>
                    )}

                    {billingAddress && (
                      <div>
                        <div className="sub-header">{fields.BillingAddressText?.value}</div>
                        <div className="address-content">
                          {firstName && lastname && <div>{`${firstName} ${lastname}`}</div>}
                          {companyName && <div>{companyName}</div>}
                          <div>{billingAddress.street1}</div>
                          <div>{billingAddress.street2}</div>
                          <div>{`${billingAddress.city}, ${billingAddress.state} ${billingAddress.zip}`}</div>
                          <div>{billingAddress.country?.toUpperCase()}</div>
                          <div>{billingAddress.phone}</div>
                        </div>
                      </div>
                    )}
                  </div>

                  <br />
                  <div className="border-100">
                    <Grid container spacing={3}>
                      <Grid item>
                        <div className="column-header">{'Date Ordered'}</div>
                        {convertDate(dateOrdered ?? '')}
                      </Grid>
                      <Grid item>
                        <div className="column-header">Invoice Number</div>
                        {orderId}
                      </Grid>
                      {receiptItems && receiptItems[0] && receiptItems[0].icpContactId && (
                        <Grid item>
                          <div className="column-header">ICP ID</div>
                          {receiptItems[0].icpContactId}
                        </Grid>
                      )}

                      <Grid item>
                        <div className="column-header">Order Total</div>
                        {getCurrencyInUSD(total)}
                      </Grid>
                      <Grid item>
                        <div className="column-header">Order Status</div>
                        {state?.sitecoreDictionaryData?.[paymentReceipt?.status]}
                      </Grid>
                      {!isNoCostOrder && (
                        <Grid item>
                          <div className="column-header">Payment Type</div>
                          {paymentType === PURCHASE_ORDER
                            ? PAYMENT_TYPES[paymentReceipt?.payment?.paymentMethod]
                            : paymentDescription}
                        </Grid>
                      )}
                    </Grid>
                  </div>

                  <br />
                  {receiptItems.map((item) => (
                    <div key={nextId()}>
                      <div className="flex-start-justifybetween title-section">
                        <div>
                          <div className="sub-header">
                            {item.productTitle}
                            {addEllipsis(item.productTitle)}
                          </div>
                          {item.productCode && lobOrderReceipt.lob !== TRAINING_IDENTIFIER && (
                            <div>Product Code: {item.productCode}</div>
                          )}
                        </div>

                        <div className="item-total">
                          <div className="sub-header" key={nextId()}>
                            {getCurrencyInUSD(Number(item.quantity) * Number(item.unitPrice))}
                          </div>
                          {item?.surchargeType && (
                            <div className="sub-header" key={nextId()}>
                              <span>{item?.surchargeType}: </span>
                              {getCurrencyInUSD(Number(item?.surchargeCost))}
                            </div>
                          )}
                        </div>
                      </div>
                      <Grid container spacing={3} className="detail-section">
                        {item.format && (
                          <Grid item style={{ minWidth: '25%' }}>
                            <div className="column-header">Format</div>
                            {item.format}
                          </Grid>
                        )}
                        <Grid item style={{ minWidth: '15%' }}>
                          <div className="column-header">Quantity</div>
                          {item.quantity}
                        </Grid>
                        {item.language && (
                          <Grid item style={{ minWidth: '15%' }}>
                            <div className="column-header">Language</div>
                            {item.language}
                          </Grid>
                        )}
                        {item.modality && (
                          <Grid item style={{ minWidth: '15%' }}>
                            <div className="column-header">Modality</div>
                            {MODALITY_TITLES[item.modality]}
                          </Grid>
                        )}
                        {item.examWindow && (
                          <Grid item style={{ minWidth: '15%' }}>
                            <div className="column-header">Exam Window</div>
                            {item.examWindow}
                          </Grid>
                        )}
                        {item.isDigitalItem && item.digitalCopiesSendAddresses?.length > 0 && (
                          <Grid item>
                            <div className="column-header">
                              {item.digitalCopySent ? 'Send to' : 'Recipient Email(s)'}
                            </div>
                            {displayEmailAddresses(item.digitalCopiesSendAddresses)}
                          </Grid>
                        )}
                        {item.isShipmentItem && item.shipToAddress && (
                          <Grid item>
                            <div className="column-header">Shipped to</div>
                            <div>
                              {Object.entries(item.shipToAddress).map(([key, value]) => (
                                <span key={nextId()}>{value && `${value}, `}</span>
                              ))}
                            </div>
                          </Grid>
                        )}
                      </Grid>
                      <Grid container spacing={3} className="detail-section detail-date-section">
                        {item.sessionStartDate && (
                          <Grid item className="session-details">
                            <div className="column-header session-dates ">Starts: </div>
                            {getDateTimePartsFromIso(item?.sessionStartDate)?.date}
                          </Grid>
                        )}
                        {item.sessionEndDate && (
                          <Grid item className="session-details">
                            <div className="column-header session-dates">Ends: </div>
                            {getDateTimePartsFromIso(item?.sessionEndDate)?.date}
                          </Grid>
                        )}
                      </Grid>
                      {item.sessionDescription && (
                        <Grid container spacing={3} className="description-section">
                          <Grid item>
                            <div className="column-header">Description: </div>
                            <span
                              className="product-value session-desc"
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(item.sessionDescription || ''),
                              }}
                            />
                          </Grid>
                        </Grid>
                      )}
                      <br />
                      <hr />
                    </div>
                  ))}
                  <br />
                  <div className="flex-column-100">
                    <div className="flex-start-justifybetween sub-header">
                      <div>Subtotal</div>
                      <div>{getCurrencyInUSD(subTotal)}</div>
                    </div>

                    {memberDiscount && memberDiscount > 0 ? (
                      <div className="flex-start-justifybetween sub-header">
                        <div>Member Discount (30%)</div>
                        <div>{getCurrencyInUSD(memberDiscount)}</div>
                      </div>
                    ) : (
                      <></>
                    )}
                    <div className="flex-start-justifybetween sub-header">
                      <div>Tax</div>
                      <div>{getCurrencyInUSD(taxTotal)}</div>
                    </div>
                    <div className="flex-start-justifybetween sub-header">
                      <div>Shipping and Handling</div>
                      <div>{getCurrencyInUSD(shippingTotal)}</div>
                    </div>
                  </div>

                  <br />
                  <hr />
                  <div className="flex-start-justifybetween sub-header">
                    <div>TOTAL</div>
                    <div>{getCurrencyInUSD(total)}</div>
                  </div>

                  {getLobConfig(lobOrderReceipt.lob)?.fields?.Stamp &&
                    getSealSection(
                      paymentType,
                      isVerifoneOrder,
                      getLobConfig(lobOrderReceipt.lob)?.fields?.Stamp
                    )}

                  <div className="paid-info">{paymentProcessed ? 'PAID IN FULL' : ''}</div>

                  {isVerifoneOrder && (
                    <div className="markupNote">
                      <span
                        dangerouslySetInnerHTML={{
                          __html: state?.sitecoreDictionaryData?.InvoiceMarkupNote || '',
                        }}
                      ></span>
                    </div>
                  )}

                  {paymentReceipt &&
                    getInvoiceAddressSection(
                      paymentReceipt.payment.paymentType,
                      paymentProcessed,
                      isVerifoneOrder,
                      lobOrderReceipt.lob,
                      orderId
                    )}

                  <div
                    key={nextId()}
                    className="flex-start-justifybetween sub-header sub-header-no-bold"
                  >
                    {paymentReceiptDetails &&
                      getFederalTaxInfo(
                        getLobConfig(lobOrderReceipt.lob)?.fields?.FederalTaxInstructions
                      )}
                  </div>
                </div>
              </div>
            );
          });
        })}
      </>
    );
  };

  return (
    <div className="unified-invoice">
      {isLoading ? (
        <div className="invoiceLoadingSection">
          <LoadingSection open={isLoading} size="fullSize" message="Please wait..." />
        </div>
      ) : !paymentReceiptDetails?.lobOrderReceipts ||
        paymentReceiptDetails.lobOrderReceipts.length === 0 ? (
        <>
          <div className="invoice-container">
            <div className="base-print-size">
              <div className="invoice-receipt" style={{ fontSize: downloadWidth }}>
                <div className="invoice-content">
                  <div className="header red-content">Reference Number Not Found</div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <div className="invoice-container" ref={printRef}>
            <div className="base-print-size">
              <div className="invoice-receipt" style={{ fontSize: downloadWidth }}>
                <InvoicePrinting />
              </div>
            </div>
          </div>

          <div className="printAndDownload">
            <ReactToPrint
              trigger={() => (
                <Button variant="contained" name="print" className="ws-primary-button-no-border">
                  {fields.PrintButtonText.value}
                </Button>
              )}
              content={() => printRef.current}
            />

            <Button
              variant="contained"
              name="download"
              className="ws-white-button"
              {...(!isWorking ? { onClick: handleDownload } : {})}
            >
              {isWorking ? <LoadingIcon /> : fields.DownloadButtonText.value}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default UnifiedPaymentReceipt;
